import React, { useState } from 'react';
import { ENV } from '../../config/env';

// Define subscription plan types and pricing details
interface PlanOption {
  id: string;
  name: string;
  price: number;
  displayPrice?: string;
  currency: string;
  per: string;
  discount: number | null;
  monthlyEquivalent?: string;
  saveText: string | null;
}

const PricingSection: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<string>('monthly'); // Default to monthly
  const isLoggedIn = localStorage.getItem('token');
  
  const planOptions: PlanOption[] = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 19,
      currency: '£',
      per: 'month',
      discount: null,
      saveText: null
    },
    {
      id: 'annual',
      name: 'Annual',
      price: 182.40,  // £19 × 12 months with 20% discount = £19 × 12 × 0.8 = £182.40
      displayPrice: '182.40',
      monthlyEquivalent: '£15.20/mo',
      currency: '£',
      per: 'year',
      discount: 20,
      saveText: 'Save 20%'
    }
  ];

  // State for email input and validation
  const [email, setEmail] = useState<string>('');
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // Validate email format
  const validateEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Handler for Stripe checkout using guest checkout endpoint
  const handleCheckout = async () => {
    const plan = planOptions.find(p => p.id === selectedPlan);
    if (!plan) return;
    
    // If user is not logged in, show email input first
    if (!isLoggedIn) {
      if (!showEmailInput) {
        setShowEmailInput(true);
        return;
      }
      
      // Validate email before proceeding
      if (!email) {
        setEmailError('Email is required');
        return;
      }
      
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      setEmailError(null);
      setIsProcessing(true);
      
      try {
        // Call the guest checkout endpoint
        // Create a proper URL instance to ensure correct formatting
        const baseUrl = new URL('/api/payment/guest-checkout', process.env.REACT_APP_API_URL || 'http://localhost:5000');
        const response = await fetch(baseUrl.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            planId: plan.id
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }
        
        const { url } = await response.json();
        
        // Redirect to Stripe checkout
        window.location.href = url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setEmailError('Failed to create checkout session. Please try again.');
        setIsProcessing(false);
      }
    } else {
      // User is logged in, use guest checkout with their email
      setIsProcessing(true);
      
      try {
        // Get user email from localStorage if available
        const userEmail = localStorage.getItem('userEmail') || '';
        
        // Call the guest checkout endpoint
        // Create a proper URL instance to ensure correct formatting
        const baseUrl = new URL('/api/payment/guest-checkout', process.env.REACT_APP_API_URL || 'http://localhost:5000');
        const response = await fetch(baseUrl.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            email: userEmail, // This will be overridden by the server if empty
            planId: plan.id
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }
        
        const { url } = await response.json();
        
        // Redirect to Stripe checkout
        window.location.href = url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setIsProcessing(false);
      }
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-transparent to-transparent opacity-60"></div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute left-10 top-40 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      <div className="absolute right-0 bottom-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      
      <div className="container relative mx-auto px-4 z-10">
        {/* Section header removed to save space */}
        
        {/* 7-day free trial banner - moved above pricing card */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-xl shadow-lg p-6 relative z-10">
            <div className="absolute -top-4 -right-4 bg-yellow-500 text-blue-900 font-extrabold px-4 py-1 rounded-full shadow-md transform rotate-3">
              FREE
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-2">Try Premium Features Free for 7 Days</h3>
            <p className="text-lg mb-4">Unlock all features with no commitment. See the difference for yourself!</p>
            <div className="bg-white rounded-lg p-3 shadow-inner">
              <p className="text-blue-800 font-medium">Your card will only be charged after your 7-day trial ends. Cancel anytime during trial period.</p>
            </div>
          </div>
        </div>
        
        {/* Pricing card with plan options */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300">
            {/* Card header */}
            <div className="bg-white px-6 py-8 text-center border-b-3 border-blue-500" style={{ borderBottomWidth: '3px' }}>
              <h3 className="text-2xl font-bold mb-1 text-blue-600">Premium Subscription</h3>
              <p className="text-blue-500 mt-2">Unlock your business potential today</p>
            </div>
            
            {/* Plan selection toggle */}
            <div className="px-6 pt-8 pb-4">
              <div className="flex justify-center mb-8">
                <div className="flex p-1 rounded-xl bg-gray-200/50 backdrop-blur-sm">
                  <button 
                    className={`
                      px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                      ${selectedPlan === 'monthly' 
                        ? 'bg-white text-blue-600 shadow-md' 
                        : 'text-gray-600 hover:bg-white/50'}
                    `}
                    onClick={() => setSelectedPlan('monthly')}
                  >
                    Monthly
                  </button>
                  <button 
                    className={`
                      px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                      ${selectedPlan === 'annual' 
                        ? 'bg-white text-blue-600 shadow-md' 
                        : 'text-gray-600 hover:bg-white/50'}
                    `}
                    onClick={() => setSelectedPlan('annual')}
                  >
                    Annual
                  </button>
                </div>
              </div>
              
              <div className="text-center mb-8 px-4">
                {selectedPlan === 'monthly' ? (
                  <div className="flex flex-col items-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">£19<span className="text-xl text-gray-500">/month</span></div>
                    <div className="text-gray-500">Billed monthly</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">£182.40<span className="text-xl text-gray-500">/year</span></div>
                    <div className="text-blue-600 font-medium mb-1">Just £15.20/month, billed annually</div>
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-bold text-sm">Save 20%</div>
                  </div>
                )}
              </div>
              
              {/* Features list */}
              <div className="mb-8">
                <h4 className="font-semibold text-gray-800 mb-4">Everything you need to succeed:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    'Personal AI business advisors',
                    'Unlimited AI image generation',
                    'Business idea generator',
                    'Logo generator',
                    'Social media post creator',
                    'Blog post creator',
                    'Business plan creator',
                    'Supplier finder tool',
                    'Market analysis reports',
                    'Financial projections',
                    'Competitive analysis',
                    'Regular updates',
                    'Cancel anytime'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center text-slate-700">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Email Input for Guest Users */}
              {showEmailInput && !isLoggedIn && (
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address to create your account
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`w-full px-3 py-2 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    disabled={isProcessing}
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-600">{emailError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    We'll use this to create your account after payment.
                  </p>
                </div>
              )}
              
              {/* Trial Message Banner */}
              <div className="bg-blue-50 border border-blue-200 text-blue-800 font-semibold p-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-md mr-2">FREE</span>
                  <span>7-Day Free Trial - No Charge Until Day 8</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleCheckout}
                disabled={isProcessing}
                className={`w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform transition-all hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : showEmailInput ? 'Provide Payment Details to Start Your Trial' : 'Start My 7-Day Free Trial'}
              </button>
              
              {/* Cancel anytime message */}
              <div className="text-center mt-3 text-sm text-slate-600">
                Cancel anytime during trial - no charge
              </div>
              
              {/* Stripe badge */}
              <div className="flex items-center justify-center mt-4 text-slate-500 text-sm">
                <span>Secure payment via <span className="font-medium text-blue-600 ml-1">Stripe</span></span>
              </div>
            </div>
          </div>
          
          {/* Testimonial badge */}
          <div className="absolute -right-12 top-32 bg-white shadow-lg rounded-lg p-3 transform rotate-12 hidden md:block">
            <div className="text-sm text-slate-800 font-medium">"Worth every penny!"</div>
            <div className="text-xs text-slate-500">— Sarah K.</div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h3>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="font-bold text-lg text-blue-800 mb-2">How does the 7-day free trial work?</h4>
              <p className="text-gray-700">
                After signing up, you'll enjoy a full 7-day free trial with complete access to all premium features. 
                Your payment method will only be charged when the trial period ends, and you can cancel anytime 
                during the trial period with no charge.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="font-bold text-lg text-blue-800 mb-2">Is there any commitment?</h4>
              <p className="text-gray-700">
                No long-term commitment! We operate on a pay-as-you-go basis with no contracts or hidden fees. 
                You can upgrade, downgrade, or cancel your subscription at any time through your account settings.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="font-bold text-lg text-blue-800 mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-700">
                We accept all major credit cards including Visa, Mastercard, and American Express. 
                All payments are securely processed through Stripe.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="font-bold text-lg text-blue-800 mb-2">What if I want to cancel my subscription?</h4>
              <p className="text-gray-700">
                You can cancel your subscription anytime from your account settings. After cancellation, 
                you'll maintain access until the end of your current billing period. We'll save your data for 
                30 days after cancellation in case you wish to reactivate your account.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600">Still have questions? <a href="/contact" className="text-blue-600 font-medium hover:underline">Contact our support team</a></p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
